import { useEffect } from 'react';
import { onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from '../config/firebase';
import {
  clearPushNotificationClickFromUrl,
  handleForegroundPushMessage,
  handlePushNotificationNavigation,
  readPushNotificationClickFromUrl,
  setWindowNotificationClickHandler,
} from '../services/fcmPushDisplay';
import { registerMessagingServiceWorker } from '../services/fcmWebRegistration';
import { FCM_SW_MESSAGE_TYPE } from '../utils/fcmPushPayload';
// ALERT_SOUND_DISABLED:
// import { playPushAlertEffects } from '../utils/pushAlertEffects';
// import { FCM_SW_MESSAGE_TYPE, FCM_SW_PLAY_NOTIFICATION_SOUND } from '../utils/fcmPushPayload';

/**
 * Foreground onMessage + notification clicks from the service worker.
 * @see https://firebase.google.com/docs/cloud-messaging/web/receive-messages
 */
export const useFcmPushMessageListener = ({
  clicksEnabled,
  messagesEnabled,
  openNotification,
  onNewNotification,
}) => {
  useEffect(() => {
    if (!clicksEnabled || !openNotification) {
      return undefined;
    }

    const handleNotificationClick = (message) => {
      handlePushNotificationNavigation(message, { openNotification });
    };

    setWindowNotificationClickHandler(handleNotificationClick);

    const pendingFromUrl = readPushNotificationClickFromUrl();
    if (pendingFromUrl) {
      handleNotificationClick({ data: pendingFromUrl });
      clearPushNotificationClickFromUrl();
    }

    const onServiceWorkerMessage = (event) => {
      const { type } = event?.data ?? {};

      // ALERT_SOUND_DISABLED — background/minimized chime via open portal tab:
      // if (type === FCM_SW_PLAY_NOTIFICATION_SOUND) {
      //   void playPushAlertEffects(event.data.payload);
      //   return;
      // }

      if (type !== FCM_SW_MESSAGE_TYPE) {
        return;
      }

      handleNotificationClick({
        data: event.data?.data ?? {},
        source: 'service-worker-notification-click',
      });
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
    }

    return () => {
      setWindowNotificationClickHandler(null);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
      }
    };
  }, [clicksEnabled, openNotification]);

  useEffect(() => {
    if (!messagesEnabled || !openNotification) {
      return undefined;
    }

    let unsubscribeOnMessage = () => {};

    const startMessagingListener = async () => {
      await registerMessagingServiceWorker();
      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.warn('[FCM] onMessage not attached — messaging unavailable');
        return;
      }

      unsubscribeOnMessage = onMessage(messaging, (payload) => {
        void handleForegroundPushMessage(payload, {
          onOpen: openNotification,
          onNewNotification,
        });
      });
    };

    void startMessagingListener();

    return () => {
      unsubscribeOnMessage();
    };
  }, [messagesEnabled, openNotification, onNewNotification]);
};
